import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import I18nProvider from './i18n/I18nProvider'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import './i18n/config'
import App from './App'
import './styles/index.css'
import './styles/global.css'

// Configure dayjs to use Hong Kong timezone
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Hong_Kong')

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
})

// Recover from stale lazy-loaded chunks after a frontend redeploy.
// Vite fires `vite:preloadError` when a dynamic import (e.g. the jszip chunk used
// to build HKTV waybill ZIPs) 404s because a new deploy changed the content
// hashes and removed the old chunk. A tab still running the previous build then
// fails with "Failed to fetch dynamically imported module". Reload once to pick
// up the fresh index + chunks; a sessionStorage time-guard prevents a reload
// loop if the asset is genuinely gone.
window.addEventListener('vite:preloadError', (event) => {
  try {
    const KEY = 'vite:preloadError:lastReload'
    const last = Number(sessionStorage.getItem(KEY) || '0')
    const now = Date.now()
    if (now - last > 10000) {
      sessionStorage.setItem(KEY, String(now))
      event.preventDefault()
      window.location.reload()
    }
  } catch {
    // storage blocked (private mode) → skip auto-reload to avoid any loop; the
    // caller's own error toast still prompts the user to refresh manually.
  }
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <App />
      </I18nProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
)